import cssesc from 'cssesc';
import _, {isUndefined} from 'lodash';

/*
 * Types.
 */

/** Parsed font-family property value. */
export type FontFamilyValue = ReadonlyArray<string>;

export enum FontFamilyPresetNamesEnum {
  SANS_SERIF = 'SANS_SERIF',
  SERIF = 'SERIF',
  FIXED_WIDTH = 'FIXED_WIDTH',
  WIDE = 'WIDE',
  ARIAL = 'ARIAL',
  HELVETICA = 'HELVETICA',
  TIMES_NEW_ROMAN = 'TIMES_NEW_ROMAN',
  CALIBRI = 'CALIBRI',
  COURIER_NEW = 'COURIER_NEW',
  COMIC_SANS = 'COMIC_SANS',
  GEORGIA = 'GEORGIA',
  TAHOMA = 'TAHOMA',
  TREBUCHET = 'TREBUCHET',
  VERDANA = 'VERDANA',
}

type FontFamilyPresetMap = {[key in FontFamilyPresetNamesEnum]: FontFamilyValue};

/*
 * Constants.
 */

export const fontFamilyPresets: FontFamilyPresetMap = {
  [FontFamilyPresetNamesEnum.SANS_SERIF]: ['Helvetica Neue', 'Segoe UI', 'Helvetica', 'Arial', 'sans-serif'],
  [FontFamilyPresetNamesEnum.SERIF]: ['Times New Roman', 'Times', 'serif'],
  [FontFamilyPresetNamesEnum.FIXED_WIDTH]: ['Lucida Console', 'Monaco', 'monospace'],
  [FontFamilyPresetNamesEnum.WIDE]: ['Arial Black', 'sans-serif'],
  [FontFamilyPresetNamesEnum.ARIAL]: ['Arial', 'Roboto', 'sans-serif'],
  [FontFamilyPresetNamesEnum.HELVETICA]: ['Helvetica', 'Arial', 'Roboto', 'sans-serif'],
  [FontFamilyPresetNamesEnum.TIMES_NEW_ROMAN]: ['Times New Roman', 'Times', 'Droid Serif', 'serif'],
  [FontFamilyPresetNamesEnum.CALIBRI]: ['Calibri', 'Carlito', 'PT Sans', 'Trebuchet MS', 'sans-serif'],
  [FontFamilyPresetNamesEnum.COURIER_NEW]: ['Courier New', 'Courier', 'Droid Sans Mono', 'monospace'],
  [FontFamilyPresetNamesEnum.COMIC_SANS]: ['Comic Sans MS', 'cursive'],
  [FontFamilyPresetNamesEnum.GEORGIA]: ['Georgia', 'serif'],
  [FontFamilyPresetNamesEnum.TAHOMA]: ['Tahoma', 'Geneva', 'sans-serif'],
  [FontFamilyPresetNamesEnum.TREBUCHET]: ['Trebuchet MS', 'sans-serif'],
  [FontFamilyPresetNamesEnum.VERDANA]: ['Verdana', 'sans-serif'],
};

export const defaultFontFamily: FontFamilyValue = fontFamilyPresets[FontFamilyPresetNamesEnum.SANS_SERIF];

/*
 * Helpers.
 */

export function formatFontFamily(fontFamily: FontFamilyValue): string {
  return fontFamily.map(formatFontFamilyMember).join(', ');
}

function formatFontFamilyMember(member: string): string {
  // Escape for double quotes.
  const escapedMember = cssesc(member, {
    quotes: 'double',
  });

  // If nothing was escaped and there are no special characters, we don't need to add quotes.
  if (escapedMember === member && isSafeCssIdentifier(escapedMember)) {
    return escapedMember;
  }

  return `"${escapedMember}"`;
}

function isSafeCssIdentifier(value: string): boolean {
  // https://www.w3.org/TR/CSS21/grammar.html
  return Boolean(value.match(/^[_a-z -]+$/i));
}

/** Parses a font-family property value. Does not support newlines or escapes. */
export function parseFontFamily(value: string): FontFamilyValue | undefined {
  // Don't try to parse the value if it contains any newlines or escapes, because we're gonna do a bad job.
  // https://www.w3.org/TR/CSS21/grammar.html
  if (!value.match(/^[^\n\r\f\\]*$/)) {
    return undefined;
  }

  const fontFamily: Array<string> = [];

  // Extract all double quoted strings, single quoted strings, and sequences of identifiers.
  // https://www.w3.org/TR/CSS21/grammar.html
  const regExp = /[ ]*(?:"([^"]*)"|'([^']*)'|((?:[ ]*-?[_a-z\240-\377][_a-z0-9-\240-\377]*)+))[ ]*(?:,|$)/iy;

  let match;
  // eslint-disable-next-line no-cond-assign
  while ((match = regExp.exec(value))) {
    fontFamily.push(match[1] || match[2] || match[3] || '');
  }

  // The value is invalid if it's empty or there were no matches.
  if (fontFamily.length === 0) {
    return undefined;
  }

  return fontFamily;
}

// The fourth font family is weighed about half that of the first font family.
const fontFamilyMemberFallbackWeight = 0.8;

/** Returns the index of the best-matching font family, or -1 if there is no overlap at all. */
export function findNearestFontFamilyIndex(
  candidateValues: ReadonlyArray<FontFamilyValue>,
  searchValue: FontFamilyValue,
): number {
  // Normalize the search font families and assign decreasing weights based on their fallback order.
  const searchMemberWeights = new Map<string, number>();
  searchValue.forEach((member, index) => {
    const weight = fontFamilyMemberFallbackWeight ** index;
    searchMemberWeights.set(normalizeFontFamilyMember(member), weight);
  });

  let winningIndex = -1;
  let winningScore: number | undefined;

  candidateValues.forEach((candidateValue, candidateIndex) => {
    const candidateScore = _(candidateValue)
      .map((member, index) => {
        // Get the corresponding weight in the search font families, or 0 if it doesn't exist.
        const searchMemberWeight = searchMemberWeights.get(normalizeFontFamilyMember(member)) || 0;

        // Multiply it by our own weight based on our own fallback order.
        const weight = fontFamilyMemberFallbackWeight ** index;
        return searchMemberWeight * weight;
      })
      .sum();

    // If the candidate font family does not intersect at all, skip it.
    if (candidateScore === 0) {
      return;
    }

    // If this candidate does not score higher, skip it.
    if (!isUndefined(winningScore) && candidateScore <= winningScore) {
      return;
    }

    winningIndex = candidateIndex;
    winningScore = candidateScore;
  });

  return winningIndex;
}

function normalizeFontFamilyMember(member: string): string {
  return member.replace(/\W+/g, '').toLowerCase();
}
